import React from 'react';

const shimmer = 'animate-pulse bg-gray-200 rounded';

export const SkeletonText: React.FC<{ width?: string; className?: string }> = ({ width = 'w-full', className = '' }) => (
  <div className={`${shimmer} h-4 ${width} ${className}`} />
);

export const SkeletonHeading: React.FC<{ width?: string; className?: string }> = ({ width = 'w-3/4', className = '' }) => (
  <div className={`${shimmer} h-7 ${width} ${className}`} />
);

export const SkeletonAvatar: React.FC<{ size?: string; className?: string }> = ({ size = 'w-12 h-12', className = '' }) => (
  <div className={`${shimmer} ${size} rounded-full ${className}`} />
);

export const SkeletonButton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`${shimmer} h-10 w-32 rounded-md ${className}`} />
);

export const SkeletonJobCard: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6 space-y-3">
    <div className="flex justify-between items-start">
      <SkeletonHeading width="w-2/3" />
      <div className={`${shimmer} h-6 w-20 rounded-full`} />
    </div>
    <SkeletonText width="w-1/3" />
    <SkeletonText />
    <SkeletonText width="w-5/6" />
    <div className="flex gap-2 pt-2">
      <div className={`${shimmer} h-6 w-16 rounded-full`} />
      <div className={`${shimmer} h-6 w-20 rounded-full`} />
      <div className={`${shimmer} h-6 w-14 rounded-full`} />
    </div>
    <div className="flex justify-between items-center pt-2">
      <SkeletonText width="w-24" />
      <SkeletonButton />
    </div>
  </div>
);

export const SkeletonJobList: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonJobCard key={i} />
    ))}
  </div>
);

export const SkeletonStatCard: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6 space-y-2">
    <SkeletonText width="w-1/2" />
    <div className={`${shimmer} h-10 w-20`} />
    <SkeletonText width="w-2/3" />
  </div>
);

export const SkeletonDashboard: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </div>
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <SkeletonHeading width="w-1/3" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4 py-3 border-b border-gray-100">
          <SkeletonAvatar size="w-10 h-10" />
          <div className="flex-1 space-y-2">
            <SkeletonText width="w-1/2" />
            <SkeletonText width="w-1/3" />
          </div>
          <SkeletonButton />
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonProfile: React.FC = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow p-6 flex items-center space-x-6">
      <SkeletonAvatar size="w-24 h-24" />
      <div className="flex-1 space-y-3">
        <SkeletonHeading width="w-1/3" />
        <SkeletonText width="w-1/4" />
        <SkeletonText width="w-1/2" />
      </div>
    </div>
    <div className="bg-white rounded-lg shadow p-6 space-y-3">
      <SkeletonHeading width="w-1/4" />
      <SkeletonText />
      <SkeletonText width="w-5/6" />
      <SkeletonText width="w-2/3" />
    </div>
  </div>
);

export const SkeletonMessageList: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center space-x-3 p-3 rounded-lg">
        <SkeletonAvatar size="w-10 h-10" />
        <div className="flex-1 space-y-2">
          <SkeletonText width="w-1/3" />
          <SkeletonText width="w-2/3" />
        </div>
        <SkeletonText width="w-12" />
      </div>
    ))}
  </div>
);

export const SkeletonRatingCard: React.FC = () => (
  <div className="bg-white p-6 rounded shadow space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex items-center space-x-3">
        <SkeletonAvatar size="w-10 h-10" />
        <div className="space-y-2">
          <SkeletonText width="w-24" />
          <SkeletonText width="w-20" />
        </div>
      </div>
      <div className="flex space-x-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`${shimmer} w-4 h-4 rounded`} />
        ))}
      </div>
    </div>
    <SkeletonText />
    <SkeletonText width="w-3/4" />
  </div>
);

export const SkeletonRatings: React.FC = () => (
  <div className="space-y-6">
    <div className="bg-white p-8 rounded shadow">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="text-center space-y-2">
            <div className={`${shimmer} h-10 w-16 mx-auto`} />
            <SkeletonText width="w-2/3" className="mx-auto" />
          </div>
        ))}
      </div>
    </div>
    {Array.from({ length: 3 }).map((_, i) => (
      <SkeletonRatingCard key={i} />
    ))}
  </div>
);

export const SkeletonTransactionList: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`${shimmer} w-12 h-12 rounded-full`} />
          <div className="space-y-2">
            <SkeletonText width="w-40" />
            <SkeletonText width="w-24" />
          </div>
        </div>
        <div className="space-y-2 text-right">
          <div className={`${shimmer} h-6 w-20 ml-auto`} />
          <div className={`${shimmer} h-5 w-16 rounded ml-auto`} />
        </div>
      </div>
    ))}
  </div>
);

export const SkeletonSubscription: React.FC = () => (
  <div className="space-y-8">
    <div className="text-center space-y-3">
      <SkeletonHeading width="w-1/3" className="mx-auto h-9" />
      <SkeletonText width="w-1/2" className="mx-auto" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl shadow-lg p-8 space-y-4">
          <SkeletonHeading width="w-1/3" />
          <div className={`${shimmer} h-12 w-24`} />
          <div className="space-y-3 pt-2">
            {Array.from({ length: 3 + i }).map((_, j) => (
              <div key={j} className="flex items-center space-x-3">
                <div className={`${shimmer} w-5 h-5 rounded-full`} />
                <SkeletonText width="w-3/4" />
              </div>
            ))}
          </div>
          <div className={`${shimmer} h-12 w-full rounded-lg mt-4`} />
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonJobDetail: React.FC = () => (
  <div className="max-w-4xl mx-auto space-y-6">
    <div className="bg-white rounded-lg shadow p-8 space-y-4">
      <SkeletonHeading width="w-3/4" className="h-9" />
      <div className="flex gap-4">
        <SkeletonText width="w-32" />
        <SkeletonText width="w-24" />
        <SkeletonText width="w-20" />
      </div>
      <div className="space-y-2 pt-4">
        <SkeletonText />
        <SkeletonText />
        <SkeletonText width="w-5/6" />
        <SkeletonText width="w-4/6" />
      </div>
      <div className="flex gap-2 pt-4">
        <div className={`${shimmer} h-8 w-20 rounded-full`} />
        <div className={`${shimmer} h-8 w-24 rounded-full`} />
        <div className={`${shimmer} h-8 w-16 rounded-full`} />
      </div>
    </div>
    <div className="bg-white rounded-lg shadow p-6 space-y-3">
      <SkeletonHeading width="w-1/4" />
      <SkeletonText width="w-1/2" />
      <SkeletonText width="w-1/3" />
    </div>
  </div>
);
